import { Injectable } from '@angular/core'
import { Inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, TokenManagerInterface, AccessToken } from '@okta/okta-auth-js';
import { ErrorHandlerService } from './core/error-handler';
import { VersionService } from '@spotlight/shared/version';
import { AnalyticsService } from './core/analytics';
import { DebugLoggerService } from './core/debug-logger.service';
import { LoggedOutGuard } from './logged-out/logged-out.guard';

@Injectable()
export class NavigationService {
  private static isLoggingOut = false;
  tokenManager: TokenManagerInterface;
  accessToken: AccessToken;

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private ehs: ErrorHandlerService,
    private readonly versionService: VersionService,
    private readonly analyticsService: AnalyticsService,
    private readonly logger: DebugLoggerService,
    private readonly router: Router
  ) { }

  public static getIsLoggingOut(): boolean {
    const isLoggingOut = NavigationService.isLoggingOut;
    // Note: Can't use instance logger here since this is a static method
    console.log('[NavigationService] getIsLoggingOut:', isLoggingOut);
    return isLoggingOut;
  }

  getCfaAudience(): string {
    const token = this.oktaAuth.getAccessToken();
    if (!token) {
      return '';
    }
    const claims = this.oktaAuth.token.decode(token).payload;
    const cfaAud = claims['cfa_aud'];
    return typeof cfaAud === 'string' ? cfaAud : '';
  }

  checkWasPerm(functionTag: string) {
    this.logger.info('[NavigationService] checkWasPerm called:', { functionTag });
    const token = this.oktaAuth.getAccessToken();
    if (!token) {
      this.logger.warn('[NavigationService] Access token is not present.');
      console.warn('Access token is not present.');
      return false;
    }

    const claims = this.oktaAuth.token.decode(token).payload;
    const perms = claims['cfa_perms']['SPOTLIGHT'];
    const hasPerm = perms ? perms[functionTag] != undefined : false;

    this.logger.info('[NavigationService] Permission check result:', {
      functionTag,
      hasPerm,
      cfaAudience: claims['cfa_aud'],
      availablePerms: perms ? Object.keys(perms) : []
    });

    this.analyticsService.setUserAttribute('CFA Audience', claims['cfa_aud']);
    return hasPerm;
  }

  public async signOut() {
    this.logger.info('[NavigationService] signOut initiated');
    NavigationService.isLoggingOut = true;

    try {
      // Clear tokens from Okta token manager (clears cookies)
      this.oktaAuth.tokenManager.clear();
      this.logger.info('[NavigationService] Token manager cleared');

      // Close the Okta session without redirecting (local signout only)
      await this.oktaAuth.closeSession();
      this.logger.info('[NavigationService] Okta session closed');

      // Allow access to logged-out page and navigate
      LoggedOutGuard.setAllowAccess(true);
      await this.router.navigate(['/logged-out']);
      this.logger.info('[NavigationService] Navigated to logged-out page');

      // Reset flag on successful sign-out so future logins/redirects work
      NavigationService.isLoggingOut = false;
    } catch (error) {
      this.logger.error('[NavigationService] signOut failed:', error);
      NavigationService.isLoggingOut = false;

      // Even if closeSession fails, still navigate to logged-out page
      LoggedOutGuard.setAllowAccess(true);
      await this.router.navigate(['/logged-out']);

      return this.ehs.handleError(error);
    }
  }

  public setup() {
    const token = this.oktaAuth.getAccessToken();
    const tokenPresent = token != undefined;

    this.logger.info('[NavigationService] setup called:', { tokenPresent, appVersion: this.versionService.getVersion() });

    return Promise.resolve(tokenPresent);
  }
}
